// src/components/GamifiedPromoBanner.js
import React, { useState, useEffect } from 'react';
import { CloseOutlined, CheckCircleFilled, CheckCircleOutlined, RocketOutlined, FireOutlined, TrophyOutlined, GiftOutlined } from '@ant-design/icons';
import axios from 'axios';
import '../styles/GamifiedPromoBanner.css';

const GamifiedPromoBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [emailCollected, setEmailCollected] = useState(false);
  const [email, setEmail] = useState('');
  const [userProgress, setUserProgress] = useState({
    firstWorkflow: false,
    threeWorkflows: false,
    tenExecutions: false,
    wroteAboutUs: false
  });
  const [userStats, setUserStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);

  const milestones = [
    {
      id: 'firstWorkflow',
      title: 'Create your first workflow',
      icon: <RocketOutlined />,
      check: (stats) => stats.workflowCount >= 1
    },
    {
      id: 'threeWorkflows', 
      title: 'Build 3 workflows',
      icon: <FireOutlined />,
      check: (stats) => stats.workflowCount >= 3
    },
    {
      id: 'tenExecutions',
      title: 'Run workflows 10 times',
      icon: <TrophyOutlined />,
      check: (stats) => stats.executionCount >= 10
    },
    {
      id: 'wroteAboutUs',
      title: 'Write about your experience',
      icon: <GiftOutlined />,
      check: () => localStorage.getItem('wpAiWorkflowsSubmittedPost') === 'true',
      locked: true // Only unlocks when other tasks complete
    }
  ];

  useEffect(() => {
    // Check if email already collected
    const savedEmail = localStorage.getItem('wpAiWorkflowsPromoEmail');
    if (savedEmail) {
      setEmailCollected(true);
    }
    
    // Check if returning from submission
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('submission') === 'success') {
      localStorage.setItem('wpAiWorkflowsSubmittedPost', 'true');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    fetchUserStats();
  }, []);

  useEffect(() => {
    if (userStats && !isLoading) {
      updateProgress();
      checkBannerVisibility();
    }
  }, [userStats, isLoading]);

  const fetchUserStats = async () => {
    try {
      setIsLoading(true);
      
      // Fetch workflows
      const workflowsResponse = await axios.get(
        `${window.wpAiWorkflowsSettings.root}wp-ai-workflows/v1/workflows`,
        {
          headers: {
            'X-WP-Nonce': window.wpAiWorkflowsSettings.nonce
          }
        }
      );
      
      // Fetch executions
      const executionsResponse = await axios.get(
        `${window.wpAiWorkflowsSettings.root}wp-ai-workflows/v1/executions`,
        {
          headers: {
            'X-WP-Nonce': window.wpAiWorkflowsSettings.nonce
          },
          params: {
            per_page: 100 // Get more to count total
          }
        }
      );
      
      const workflows = workflowsResponse.data || [];
      const executions = executionsResponse.data?.executions || [];
      
      const stats = {
        workflowCount: workflows.length,
        executionCount: executions.length
      };
      
      setUserStats(stats);
      
    } catch (error) {
      console.error('Error fetching user stats:', error);
      setUserStats({
        workflowCount: 0,
        executionCount: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateProgress = () => {
    if (!userStats) return;
    
    const newProgress = {
      firstWorkflow: milestones[0].check(userStats),
      threeWorkflows: milestones[1].check(userStats),
      tenExecutions: milestones[2].check(userStats),
      wroteAboutUs: milestones[3].check(userStats)
    };
    
    // Check if all non-write tasks are complete
    const tasksComplete = newProgress.firstWorkflow && 
                         newProgress.threeWorkflows && 
                         newProgress.tenExecutions;
    
    // Show confetti when all tasks complete for the first time
    if (tasksComplete && !userProgress.wroteAboutUs && !newProgress.wroteAboutUs) {
      const hasSeenConfetti = localStorage.getItem('wpAiWorkflowsSeenConfetti');
      if (!hasSeenConfetti) {
        setShowConfetti(true);
        localStorage.setItem('wpAiWorkflowsSeenConfetti', 'true');
        setTimeout(() => setShowConfetti(false), 5000);
      }
    }
    
    setUserProgress(newProgress);
  };

  const checkBannerVisibility = () => {
    // Only show for Lite version
    if (process.env.REACT_APP_VERSION !== 'lite') {
      setIsVisible(false);
      return;
    }

    // Hide forever if user completed everything
    if (userProgress.wroteAboutUs) {
      setIsVisible(false);
      return;
    }

    // Always show if not completed (no dismissal)
    setTimeout(() => {
      setIsVisible(true);
      setIsAnimating(true);
      trackEvent('gamified_banner_shown');
    }, 2000);
  };

  const trackEvent = (action, details = {}) => {
    if (window.gtag) {
      window.gtag('event', action, {
        'event_category': 'Gamified Promo Banner',
        'progress': Object.values(userProgress).filter(Boolean).length,
        'workflow_count': userStats?.workflowCount || 0,
        'execution_count': userStats?.executionCount || 0,
        ...details
      });
    }
  };

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMinimized(true);
    trackEvent('banner_minimized');
  };

    const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      // Get installation ID from settings or generate one
      const installationId = window.wpAiWorkflowsSettings?.installation_id || 
                           localStorage.getItem('wp_ai_workflows_installation_id') ||
                           'unknown';
      
      try {
        // Send to analytics endpoint like other analytics data
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        await fetch('https://wpaiworkflows.com/wp-json/wp-ai-workflows-analytics/v1/collect-email', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-WP-AI-Workflows': 'gamification-email'
          },
          body: JSON.stringify({
            installation_id: installationId,
            email: email,
            event: 'gamification_email_collected',
            source: 'lite_gamification_banner',
            domain: window.location.hostname,
            workflow_count: userStats?.workflowCount || 0,
            execution_count: userStats?.executionCount || 0,
            timestamp: new Date().toISOString()
          }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
      } catch (error) {
        console.log('Email submission failed:', error);
        // Continue anyway - don't block user experience
      }
      
      localStorage.setItem('wpAiWorkflowsPromoEmail', email);
      setEmailCollected(true);
      trackEvent('email_collected');
    }
  };

  const getTaskHelpText = (taskId, isActive) => {
    if (!isActive) return null;
    
    const helpTexts = {
      firstWorkflow: "Head to the Workflow Builder and create a new workflow and save it",
      threeWorkflows: "Use the workflow generator or templates to create more complex workflows",
      tenExecutions: "For example, Try making automated posts with online research or run your workflows",
      wroteAboutUs: "Click below to share your amazing workflows with our community"
    };
    
    return helpTexts[taskId];
  };

  const isOnBuilderPage = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const action = urlParams.get('action');
    
    // Check if we're on the builder or edit action
    if (action === 'builder' || action === 'edit') return true;
    
    return false;
  };

  const handleWriteAboutUs = () => {
    trackEvent('write_about_us_clicked');
    
    // Get installation ID
    const installationId = window.wpAiWorkflowsSettings?.installation_id || 
                         localStorage.getItem('wp_ai_workflows_installation_id') ||
                         'unknown';
    
    // Build redirect URL for after form submission
    const currentDomain = window.location.hostname;
    const redirectUrl = `${window.location.protocol}//${currentDomain}/wp-admin/admin.php?page=wp-ai-workflows`;
    
    // Build the full URL with all parameters
    const params = new URLSearchParams({
      utm_source: 'wp-ai-workflows-lite',
      utm_medium: 'plugin',
      utm_campaign: 'gamified-promo',
      utm_content: 'final-step',
      email: localStorage.getItem('wpAiWorkflowsPromoEmail') || '',
      installation_id: installationId,
      domain: currentDomain
    });
    
    window.open(
      `https://wpaiworkflowautomation.com/share-your-success?${params.toString()}`,
      '_blank'
    );
  };

  const getProgressPercentage = () => {
    const completed = Object.values(userProgress).filter(Boolean).length;
    return (completed / 4) * 100;
  };

  const getMotivationalMessage = () => {
    const completed = Object.values(userProgress).filter(Boolean).length;
    
    if (completed === 0) {
      return "Start your journey to PRO! 🚀";
    } else if (completed === 1) {
      return "Great start! Keep going! 💪";
    } else if (completed === 2) {
      return "You're halfway there! 🔥";
    } else if (completed === 3 && !userProgress.wroteAboutUs) {
      return "🎉 You qualify! Share your experience to claim PRO!";
    } else if (completed === 4) {
      return "🏆 Thank you! We'll send your PRO license soon!";
    }
  };

  if (!isVisible || isLoading) return null;

  const allTasksComplete = userProgress.firstWorkflow && 
                          userProgress.threeWorkflows && 
                          userProgress.tenExecutions;

  // Minimized view
  if (isMinimized) {
    return (
      <div 
        className={`gamified-banner-minimized ${isOnBuilderPage() ? 'builder-page' : ''}`} 
        onClick={() => setIsMinimized(false)}
      >
        <div className="minimized-content">
          <span className="minimized-icon">🎯</span>
          <span className="minimized-text">Steps to Free Pro License</span>
          <div className="minimized-progress">
            <div 
              className="minimized-progress-fill" 
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>
          <span className="minimized-count">
            {Object.values(userProgress).filter(Boolean).length}/4
          </span>
        </div>
      </div>
    );
  }

  // Email collection view
  if (!emailCollected) {
    return (
      <div className={`gamified-banner gamified-banner-email ${isAnimating ? 'gamified-banner-visible' : ''}`}>
        <button 
          className="gamified-banner-close" 
          onClick={handleDismiss}
          aria-label="Dismiss banner"
        >
          <CloseOutlined />
        </button>
        
        <div className="email-collection">
          <h3 className="email-title">🎁 Unlock 3 Months PRO Free!</h3>
          <p className="email-subtitle">Complete simple tasks and get rewarded</p>
          <form onSubmit={handleEmailSubmit} className="email-form">
            <input
              type="email"
              placeholder="Enter your email to start"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="email-input"
              required
            />
            <button type="submit" className="email-submit">
              Start →
            </button>
          </form>
          <p className="email-privacy">
            By entering your email, you agree to our{' '}
            <a href="https://wpaiworkflowautomation.com/privacy-policy/" target="_blank" rel="noopener noreferrer">
              privacy policy
            </a>
          </p>
        </div>
      </div>
    );
  }

  // Main gamified view

  return (
    <div className={`gamified-banner ${isAnimating ? 'gamified-banner-visible' : ''} ${isOnBuilderPage() ? 'builder-page' : ''}`}>
      <button 
        className="gamified-banner-close" 
        onClick={handleDismiss}
        aria-label="Minimize banner"
      >
        <CloseOutlined />
      </button>
      
      {showConfetti && (
        <div className="confetti-container">
          {[...Array(30)].map((_, i) => (
            <div key={i} className={`confetti confetti-${i % 6}`} />
          ))}
        </div>
      )}
      
      <div className="gamified-banner-header">
        <h3 className="gamified-banner-title">
          Unlock 3 Months PRO Free!
        </h3>
        <p className="gamified-banner-subtitle">{getMotivationalMessage()}</p>
      </div>
      
      <div className="gamified-banner-progress">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${getProgressPercentage()}%` }}
          />
        </div>
        <span className="progress-text">
          {Object.values(userProgress).filter(Boolean).length}/4 completed
        </span>
      </div>
      
      <div className="gamified-banner-tasks">
        {milestones.map((milestone, index) => {
          const isCompleted = userProgress[milestone.id];
          const isLocked = milestone.locked && !allTasksComplete;
          const isActive = !isCompleted && !isLocked && 
                          (index === 0 || userProgress[milestones[index - 1].id]);
          
          return (
            <div 
              key={milestone.id}
              className={`task-item ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
            >
              <div>
                <div className="task-icon">
                  {isCompleted ? (
                    <CheckCircleFilled style={{ color: '#52c41a', fontSize: '18px' }} />
                  ) : isLocked ? (
                    <span className="lock-icon">🔒</span>
                  ) : (
                    <CheckCircleOutlined style={{ fontSize: '18px' }} />
                  )}
                </div>
                <div className="task-content">
                  <span className="task-title">{milestone.title}</span>
                  {milestone.id === 'firstWorkflow' && !isCompleted && (
                    <span className="task-hint">({userStats.workflowCount}/1)</span>
                  )}
                  {milestone.id === 'threeWorkflows' && !isCompleted && (
                    <span className="task-hint">({userStats.workflowCount}/3)</span>
                  )}
                  {milestone.id === 'tenExecutions' && !isCompleted && (
                    <span className="task-hint">({userStats.executionCount}/10)</span>
                  )}
                  {milestone.id === 'wroteAboutUs' && !isLocked && !isCompleted && (
                    <button 
                      className="task-action-button"
                      onClick={handleWriteAboutUs}
                    >
                      Share →
                    </button>
                  )}
                </div>
              </div>
              {isActive && (
                <div className="task-help-text">
                  {getTaskHelpText(milestone.id, isActive)}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {allTasksComplete && !userProgress.wroteAboutUs && (
        <div className="gamified-banner-cta">
          <button className="cta-button" onClick={handleWriteAboutUs}>
            <GiftOutlined /> Share What You Created
          </button>
          <p className="cta-note">Tell us about your amazing workflows</p>
        </div>
      )}
    </div>
  );
};

export default GamifiedPromoBanner;